import React from "react";
import styled from "styled-components";
import { SortScope } from "spraypaint";
import { useQueryClient } from "react-query";
import { humanizeSalaryRange, humanizeTimeAgo } from "../helpers/formatters";
import JobStatusTag from "./JobStatusTag";
import RecruiterJobPreviewDrawer from "./RecruiterJobPreviewDrawer";
import { SortDir } from "spraypaint/lib-esm/scope";
import { capitalize } from "lodash";
import useQueryParam from "../hooks/useQueryParam";
import ReactTooltip from "react-tooltip";
import Button from "./library/Button";
import Loader from "./library/Loader";
import { SortableTh, Table, Td, Th, Thead, Tr } from "./library/Table";
import JobTitleTd from "./JobTitleTd";
import Job, { JobType } from "../apiResources/Job";
import { useRecruiter } from "../api/company";
import { useFlash } from "../hooks";
import FlashMessage from "./shared/general/FlashMessage";

type RecruiterJobsTableProps = {
  jobs: Job[];
  sort: SortScope;
  onSortChange: (SortScope) => void;
};

const RecruiterJobsTable = (props: RecruiterJobsTableProps) => {
  const { jobs, sort, onSortChange } = props;
  const queryClient = useQueryClient();

  const [jobId, setJobId] = useQueryParam<string>("jobId", undefined, {
    keepChangesInHistory: true,
  });

  const { data: recruiter, isLoading, isIdle } = useRecruiter();
  const flash = useFlash();

  const sortBy = (key: string) => {
    const dir: SortDir = sort[key] === "asc" ? "desc" : "asc";
    onSortChange({ [key]: dir });
  };

  if (jobs.length === 0) {
    return (
      <div>Sorry, we were unable to find any jobs that match your search</div>
    );
  }

  if (isIdle || isLoading) {
    return <Loader />;
  }

  const onEngageSuccess = () => {
    flash.show();
    setJobId(null);
    queryClient.invalidateQueries("jobs");
    queryClient.invalidateQueries("jobPreview");
  };

  const handleRowClick = (job: Job) => {
    if (Boolean(disabledReason(job))) {
      return;
    }

    setJobId(job.id);
  };

  const disabledReason = (job: Job) => {
    if (recruiter.isNotAllowedToEngage) {
      return recruiter.isNotAllowedToEngageReason;
    }

    if (!recruiter.canEngageOnContracts && job.jobType === JobType.CONTRACT) {
      return `Your account is unable to engage on contract roles.<br>
      Please contact your account manager.`;
    }
  };

  return (
    <>
      <FlashMessage
        success
        showFlashMessage={flash.visible}
        message="Engagement request has been sent"
      />
      <TableWrapper>
        <ReactTooltip
          multiline
          id="job-search-table-tooltip"
          place="bottom"
          type="dark"
          effect="solid"
          aria-haspopup="true"
        />
        <Table id="recruiter-job-search-results">
          <Thead>
            <Tr>
              <SortableTh name="title" onClick={sortBy} dir={sort["title"]}>
                Title
              </SortableTh>
              <SortableTh
                collapse="md"
                name="location"
                onClick={sortBy}
                dir={sort["location"]}
              >
                Location
              </SortableTh>
              <SortableTh
                collapse="sm"
                name="salaryMin"
                onClick={sortBy}
                dir={sort["salaryMin"]}
              >
                Salary
              </SortableTh>
              <SortableTh
                name="placementFeePercent"
                onClick={sortBy}
                dir={sort["placementFeePercent"]}
              >
                Fee
              </SortableTh>
              <Th collapse="md">Sector</Th>
              <SortableTh
                name="jobType"
                onClick={sortBy}
                dir={sort["jobType"]}
                collapse="md"
              >
                Type
              </SortableTh>
              <SortableTh name="status" onClick={sortBy} dir={sort["status"]}>
                Status
              </SortableTh>
              <SortableTh
                collapse="sm"
                name="listedAt"
                onClick={sortBy}
                dir={sort["listedAt"]}
              >
                Listed
              </SortableTh>
              <Th />
            </Tr>
          </Thead>
          <tbody>
            {jobs.map((job) => (
              <Tr
                key={job.id}
                onClick={disabledReason(job) ? null : () => handleRowClick(job)}
              >
                <JobTitleTd job={job} />
                <Td collapse="md">{job.location}</Td>
                <Td collapse="sm">
                  {humanizeSalaryRange(
                    job.salaryMin,
                    job.salaryMax,
                    job.currency?.isoCode
                  )}
                </Td>
                <Td>{job.placementFeePercent}%</Td>
                <Td collapse="md">{job.sector?.name}</Td>
                <Td collapse="md">{capitalize(job.jobType)}</Td>
                <Td>
                  <JobStatusTag status={job.status} />
                </Td>
                <Td collapse="sm">{humanizeTimeAgo(job.listedAt)}</Td>
                <Td>
                  <div
                    data-for="job-tag-tooltip"
                    data-tip={disabledReason(job)}
                  >
                    <Button
                      primary
                      disabled={Boolean(disabledReason(job))}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(job);
                      }}
                    >
                      View
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <RecruiterJobPreviewDrawer
          isOpen={Boolean(jobId)}
          onEngageSuccess={onEngageSuccess}
          onClose={() => setJobId(null)}
          jobId={jobId}
        />
      </TableWrapper>
    </>
  );
};

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

export default RecruiterJobsTable;
